// Inserting HTML into the CKEditor 5 model where the author's cursor is.
//
// The public site shows media where it sits in the post (portal
// buildArticleSegments), so "Add gallery images" must put the images where the
// author is working, not always at the end of the post.

/**
 * insertAfterCursorBlock(editor, html): insert `html` right AFTER the
 * top-level block that holds the cursor (or after the selected image or
 * table, or after the whole list), never inside it. Inserting inside a
 * paragraph would make CKEditor merge the first inserted block (the "Event
 * Gallery" heading) into that paragraph's text. An empty paragraph at the
 * cursor is replaced.
 *
 * Uses the documented model API: editor.data.processor.toView,
 * editor.data.toModel, model.insertContent(content, position). The document
 * selection survives read-only mode and the editor losing focus to the
 * gallery button, so it still marks where the author was.
 *
 * Afterwards the last inserted block is selected, so a further batch goes
 * after this one.
 *
 * Returns false when the editor has no model API to do it with; the caller
 * then falls back to appending.
 */
const isElement = (node, name) => !!node && typeof node.is === "function" && node.is("element", name);

export function insertAfterCursorBlock(editor, html) {
  const model = editor && editor.model;
  const data = editor && editor.data;
  if (!model || !data || !data.processor || typeof model.insertContent !== "function") return false;

  const root = model.document.getRoot();
  const selection = model.document.selection;
  const fragment = data.toModel(data.processor.toView(html));

  model.change((writer) => {
    const position = selection.getFirstPosition();
    let at;
    let emptyLine = null;
    if (!position || position.root !== root) {
      at = writer.createPositionAt(root, "end");
    } else if (position.parent === root) {
      // Between blocks, or on a selected object (an image, a table).
      const selected = selection.getSelectedElement();
      at = selected ? writer.createPositionAfter(selected) : position;
    } else {
      let block = position.parent;
      while (block.parent && block.parent !== root) block = block.parent;
      // In CKEditor 34 every list item is its own top-level listItem element:
      // stopping at the cursor's item would cut the list in two and un-nest
      // its nested items (adversarial review). Go past the whole list: its
      // nested items (listIndent > 0) and its top-level items of the SAME
      // listType, but not into an adjacent list of another type (Codex round
      // 2: <ul> then <ol> are two lists).
      if (isElement(block, "listItem")) {
        let top = block;
        while (Number(top.getAttribute("listIndent")) > 0 && isElement(top.previousSibling, "listItem")) {
          top = top.previousSibling;
        }
        const listType = top.getAttribute("listType");
        const sameList = (item) =>
          isElement(item, "listItem") &&
          (Number(item.getAttribute("listIndent")) > 0 || item.getAttribute("listType") === listType);
        while (sameList(block.nextSibling)) block = block.nextSibling;
      }
      if (isElement(block, "paragraph") && block.isEmpty) {
        // A blank line opened for the gallery (or a new, empty post): the
        // gallery takes its place instead of leaving <p>&nbsp;</p> behind.
        emptyLine = block;
        at = writer.createPositionBefore(block);
      } else {
        at = writer.createPositionAfter(block);
      }
    }
    const inserted = model.insertContent(fragment, at);
    if (emptyLine) writer.remove(emptyLine);
    // insertContent at a given position leaves the selection where it was, so
    // a second batch would land after the same paragraph, AHEAD of the first
    // one (two carousels, in reverse order; Codex admin review). Select the
    // last inserted image: the next batch then follows it and joins the run.
    const last = inserted && inserted.end && inserted.end.nodeBefore;
    if (last) writer.setSelection(last, "on");
  });
  return true;
}
